// data/items/bossGear.js
// All equipment dropped by dungeon bosses and the Convergence Herald
// Organized by: Dungeon Boss Weapons, Eclipse Weapons, Boss Rings, Eclipse Rings,
//               Boss Shield, Eclipse Shield, Boss Focus, Boss Quiver

// ============================================================
// DUNGEON BOSS WEAPONS (4) — Ashen Warden, Pyre Guardian, Flame Eternal
// Better than crafted T6, stepping stones to Eclipse tier
// ============================================================
export const BOSS_WEAPONS = [
    {
        id: 'ashen_greatsword',
        name: 'Ashen Greatsword',
        color: '#c0392b',
        tier: 7,  // Above T6 crafted
        slot: 'weapon',
        twoHanded: true,
        attackStyle: 'melee',
        attackBonus: 135,
        attackSpeed: 1900,  // 20% faster than original - 2H melee needs speed boost
        meleeAccuracy: 45,
        combatLevel: 80,
        value: 3200,
        bossGear: true,
        dropSource: 'ashen_warden',
        // Smoldering Strikes — every 4th hit deals bonus fire damage equal to 15% of the hit
        specialEffect: {
            type: 'smolderingStrikes',
            hitInterval: 4,
            bonusDamagePercent: 0.15,
            element: 'fire',
            description: 'Every 4th hit deals bonus fire damage equal to 15% of the hit'
        }
    },
    {
        id: 'pyreguard_blade',
        name: 'Pyreguard Blade',
        color: '#e67e22',
        tier: 7,
        slot: 'weapon',
        attackStyle: 'melee',
        attackBonus: 100,
        attackSpeed: 1950,  // 15% slower - 1H trades DPS for shield
        meleeAccuracy: 36,
        combatLevel: 75,
        value: 2800,
        bossGear: true,
        dropSource: 'pyre_guardian',
        // Searing Parry — when hit, 12% chance to counter for 50% of your attack as fire damage
        specialEffect: {
            type: 'searingParry',
            procChance: 0.12,
            counterDamagePercent: 0.50,
            element: 'fire',
            description: 'When hit, 12% chance to counter for 50% of your attack as fire damage'
        }
    },
    {
        id: 'pyrebound_staff',
        name: 'Pyrebound Staff',
        color: '#e74c3c',
        tier: 7,
        slot: 'weapon',
        attackStyle: 'magic',
        attackBonus: 85,
        attackSpeed: 2700,
        magicAccuracy: 48,
        magicDamage: 2.2,  // Upgraded from 2.0x
        combatLevel: 75,
        value: 2800,
        bossGear: true,
        dropSource: 'pyre_guardian',
        // Pyre Resonance — spell effects last 25% longer
        specialEffect: {
            type: 'pyreResonance',
            spellDurationBonus: 0.25,
            description: 'Spell effects last 25% longer'
        }
    },
    {
        id: 'cindered_shortbow',
        name: 'Cindered Shortbow',
        color: '#d35400',
        tier: 7,
        slot: 'weapon',
        attackStyle: 'ranged',
        attackBonus: 82,
        attackSpeed: 2000,
        rangedAccuracy: 50,
        combatLevel: 80,
        value: 3000,
        bossGear: true,
        dropSource: 'flame_eternal',
        // Searing Arrows — 15% chance to ignite target (fire DoT: 3 dmg per 2s for 6s)
        specialEffect: {
            type: 'searingArrows',
            procChance: 0.15,
            dotDamage: 3,
            dotInterval: 2000,
            dotDuration: 6000,
            element: 'fire',
            description: '15% chance to ignite target (3 fire damage per 2s for 6s)'
        }
    }
];

// ============================================================
// ECLIPSE WEAPONS (4) — Convergence Herald drops
// Absolute endgame: ~30-35% over current BiS
// ============================================================
export const ECLIPSE_WEAPONS = [
    {
        id: 'eclipse_reaver',
        name: 'Eclipse Reaver',
        color: '#1a1a2e',
        tier: 8,  // Eclipse tier
        slot: 'weapon',
        twoHanded: true,
        attackStyle: 'melee',
        attackBonus: 165,
        attackSpeed: 1800,  // Fast endgame 2H - melee's best option
        meleeAccuracy: 55,
        critChance: 8,
        combatLevel: 90,
        value: 8000,
        eclipseGear: true,
        dropSource: 'convergence_herald',
        // +8% crit, +5% lifesteal (passive stats + effect)
        specialEffect: {
            type: 'eclipseReaver',
            lifestealPercent: 0.05,
            description: '+8% crit chance, +5% lifesteal'
        }
    },
    {
        id: 'eclipse_edge',
        name: 'Eclipse Edge',
        color: '#16213e',
        tier: 8,
        slot: 'weapon',
        attackStyle: 'melee',
        attackBonus: 120,
        attackSpeed: 1850,  // 15% slower - 1H trades DPS for shield
        meleeAccuracy: 42,
        combatLevel: 90,
        value: 8000,
        eclipseGear: true,
        dropSource: 'convergence_herald',
        // Eclipse Slash — every 3rd hit releases a wave dealing 40% of your attack as bonus damage
        specialEffect: {
            type: 'eclipseSlash',
            hitInterval: 3,
            bonusDamagePercent: 0.40,
            description: 'Every 3rd hit releases a wave dealing 40% of your attack as bonus damage'
        }
    },
    {
        id: 'eclipse_piercer',
        name: 'Eclipse Piercer',
        color: '#0f3460',
        tier: 8,
        slot: 'weapon',
        attackStyle: 'ranged',
        attackBonus: 105,
        attackSpeed: 1650,  // Fast endgame bow
        rangedAccuracy: 58,
        combatLevel: 90,
        value: 8000,
        eclipseGear: true,
        dropSource: 'convergence_herald',
        // Arrows not consumed (still need to equip for stats), +10% ranged damage
        freeAmmo: true,
        specialEffect: {
            type: 'eclipsePiercer',
            rangedDamageBonus: 0.10,
            description: 'Arrows not consumed, +10% ranged damage'
        }
    },
    {
        id: 'eclipse_conduit',
        name: 'Eclipse Conduit',
        color: '#533483',
        tier: 8,
        slot: 'weapon',
        attackStyle: 'magic',
        attackBonus: 100,
        attackSpeed: 2200,  // Fast endgame staff
        magicAccuracy: 58,
        magicDamage: 2.5,  // Highest in game
        combatLevel: 90,
        value: 8000,
        eclipseGear: true,
        dropSource: 'convergence_herald',
        // Free glyph costs (all spells work, talents apply), +10% spell damage
        freeGlyphs: true,
        specialEffect: {
            type: 'eclipseConduit',
            spellDamageBonus: 0.10,
            description: '2.5x spell multiplier, free glyph costs, +10% spell damage'
        }
    }
];

// ============================================================
// DUNGEON BOSS RINGS (3) — One per boss
// Progression: defensive → sustain → offensive
// ============================================================
export const BOSS_RINGS = [
    {
        id: 'wardens_ember_ring',
        name: "Warden's Ember Ring",
        color: '#e74c3c',
        value: 2000,
        slot: 'ring',
        combatLevel: 70,
        attackBonus: 8,
        defenseBonus: 4,
        critChance: 3,
        bossGear: true,
        dropSource: 'ashen_warden',
        // Ember Shield — when hit, 10% chance to gain +8 defense for 5s
        uniqueEffect: {
            type: 'emberShield',
            procChance: 0.10,
            defenseBonus: 8,
            duration: 5000,
            description: 'When hit, 10% chance to gain +8 defense for 5s'
        }
    },
    {
        id: 'guardians_flame_signet',
        name: "Guardian's Flame Signet",
        color: '#e67e22',
        value: 2500,
        slot: 'ring',
        combatLevel: 75,
        attackBonus: 6,
        spellDamage: 0.05,
        defenseBonus: 6,
        bossGear: true,
        dropSource: 'pyre_guardian',
        // Flame Barrier — food healing increased by 20%
        uniqueEffect: {
            type: 'flameBarrier',
            foodHealBonus: 0.20,
            description: 'Food healing increased by 20%'
        }
    },
    {
        id: 'eternal_inferno_band',
        name: 'Eternal Inferno Band',
        color: '#c0392b',
        value: 3000,
        slot: 'ring',
        combatLevel: 80,
        attackBonus: 10,
        spellDamage: 0.06,
        critChance: 5,
        bossGear: true,
        dropSource: 'flame_eternal',
        // Inferno's Fury — +20% damage to targets below 33% HP
        uniqueEffect: {
            type: 'infernosFury',
            executeDamageBonus: 0.20,
            executeThreshold: 0.33,
            description: '+20% damage to targets below 33% HP'
        }
    }
];

// ============================================================
// ECLIPSE RINGS (3) — Style-specific, Convergence Herald drops
// Each naturally synergizes with matching set bonus
// ============================================================
export const ECLIPSE_RINGS = [
    {
        id: 'eclipse_wrath_band',
        name: 'Eclipse Wrath Band',
        color: '#1a1a2e',
        value: 6000,
        slot: 'ring',
        combatLevel: 90,
        attackBonus: 14,
        defenseBonus: 8,
        critChance: 5,
        eclipseGear: true,
        dropSource: 'convergence_herald',
        // Relentless — crits increase attack speed by 3% for 5s (stacks up to 15%)
        uniqueEffect: {
            type: 'relentless',
            speedBonusPerStack: 3,
            maxStacks: 5,
            stackDuration: 5000,
            description: 'Crits increase attack speed by 3% for 5s (stacks up to 15%)'
        }
    },
    {
        id: 'eclipse_precision_band',
        name: 'Eclipse Precision Band',
        color: '#0f3460',
        value: 6000,
        slot: 'ring',
        combatLevel: 90,
        attackBonus: 12,
        defenseBonus: 6,
        critChance: 8,
        eclipseGear: true,
        dropSource: 'convergence_herald',
        // Marked Prey — crits mark target; marked targets take 12% more damage for 8s
        uniqueEffect: {
            type: 'markedPrey',
            damageAmplification: 0.12,
            markDuration: 8000,
            description: 'Crits mark the target; marked targets take 12% more damage from all sources for 8s'
        }
    },
    {
        id: 'eclipse_arcana_band',
        name: 'Eclipse Arcana Band',
        color: '#533483',
        value: 6000,
        slot: 'ring',
        combatLevel: 90,
        attackBonus: 8,
        spellDamage: 0.12,
        defenseBonus: 6,
        critChance: 5,
        eclipseGear: true,
        dropSource: 'convergence_herald',
        // Arcane Overflow — +10% dmg during spell effects, +8% spell dmg with 2+ effects
        uniqueEffect: {
            type: 'arcaneOverflow',
            baseDamageBonus: 0.10,
            multiEffectSpellBonus: 0.08,
            description: 'While a spell effect is active, all damage +10%. With 2+ simultaneous spell effects, additional +8% spell damage'
        }
    }
];

// ============================================================
// BOSS SHIELDS (2) — Ashen Bulwark + Eclipse Aegis
// ============================================================
export const BOSS_BULWARKS = [
    {
        id: 'ashen_bulwark',
        name: 'Ashen Bulwark',
        color: '#95a5a6',
        tier: 7,
        slot: 'offhand',
        offhandType: 'bulwark',
        meleeAccuracy: 0,
        defenseBonus: 92,
        hpBonus: 135,
        blockChance: 15,
        combatLevel: 75,
        value: 3500,
        bossGear: true,
        dropSource: 'pyre_guardian',
        // Warden's Resolve — hits taken have 8% chance to heal 5% of max HP
        specialEffect: {
            type: 'wardensResolve',
            procChance: 0.08,
            healPercent: 0.05,
            description: 'Hits taken have 8% chance to heal 5% of max HP'
        }
    },
    {
        id: 'eclipse_aegis',
        name: 'Eclipse Aegis',
        color: '#1a1a2e',
        tier: 8,
        slot: 'offhand',
        offhandType: 'bulwark',
        meleeAccuracy: 0,
        defenseBonus: 110,
        hpBonus: 170,
        blockChance: 18,
        combatLevel: 90,
        value: 9000,
        eclipseGear: true,
        dropSource: 'convergence_herald',
        // Eclipse Reflection — 10% of damage taken is reflected back to the attacker
        specialEffect: {
            type: 'eclipseReflection',
            reflectPercent: 0.10,
            description: '10% of damage taken is reflected back to the attacker'
        }
    }
];

// ============================================================
// BOSS FOCUS (1) — Emberheart Focus (Ashen Warden drop)
// ============================================================
export const BOSS_FOCUSES = [
    {
        id: 'emberheart_focus',
        name: 'Emberheart Focus',
        color: '#e74c3c',
        tier: 7,
        slot: 'offhand',
        offhandType: 'focus',
        spellDamage: 0.52,
        magicAccuracy: 72,
        combatLevel: 70,
        value: 3000,
        bossGear: true,
        dropSource: 'ashen_warden',
        // Smoldering Resonance — spells have 25% chance to apply fire DoT regardless of element
        specialEffect: {
            type: 'smolderingResonance',
            procChance: 0.25,
            dotDamage: 3,
            dotTicks: 4,
            dotInterval: 2000,
            element: 'fire',
            description: 'Spells have a 25% chance to apply a fire DoT (3 dmg/tick x 4 ticks) regardless of element cast'
        }
    }
];

// ============================================================
// BOSS QUIVER (1) — Cindered Quiver (Flame Eternal drop)
// ============================================================
export const BOSS_QUIVERS = [
    {
        id: 'cindered_quiver',
        name: 'Cindered Quiver',
        color: '#d35400',
        tier: 5,  // Above T4 Veilstalker
        slot: 'back',
        backType: 'quiver',
        rangedAccuracy: 64,
        speedBonus: 15,
        ammoEfficiency: 25,
        combatLevel: 80,
        value: 4000,
        bossGear: true,
        dropSource: 'flame_eternal',
        // Volatile Arrows — arrows deal 8% bonus fire damage. Crits deal 15% instead
        specialEffect: {
            type: 'volatileArrows',
            baseBonusDamage: 0.08,
            critBonusDamage: 0.15,
            element: 'fire',
            description: 'Arrows deal 8% bonus fire damage. Critical hits deal 15% instead'
        }
    }
];

// Combined export for item registry
export const ALL_BOSS_GEAR = [
    ...BOSS_WEAPONS,
    ...ECLIPSE_WEAPONS,
    ...BOSS_RINGS,
    ...ECLIPSE_RINGS,
    ...BOSS_BULWARKS,
    ...BOSS_FOCUSES,
    ...BOSS_QUIVERS
];

export default ALL_BOSS_GEAR;
